import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api.ts";
import { ErrorBanner } from "../ErrorBanner.tsx";
import type { ContentSnapshot, Report } from "../types.ts";

interface Props {
  kind: "listing" | "safety_note";
  id: string;
}

interface ContentResponse {
  content: ContentSnapshot;
  reports: Report[];
}

export function Content({ kind, id }: Props) {
  const slug = kind === "listing" ? "listing" : "safety-note";
  const [data, setData] = useState<ContentResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const body = await api<ContentResponse>(
        `/admin/content/${slug}/${encodeURIComponent(id)}`,
      );
      setData(body);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [slug, id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function toggleHide() {
    if (!data) return;
    const action = data.content.hidden ? "unhide" : "hide";
    setBusy(action);
    try {
      await api(
        `/admin/content/${slug}/${encodeURIComponent(id)}/${action}`,
        { method: "POST" },
      );
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function resolveReport(reportId: string) {
    setBusy(`resolve-${reportId}`);
    try {
      await api(
        `/admin/reports/${encodeURIComponent(reportId)}/resolve`,
        { method: "POST" },
      );
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  if (loading && !data) return <div className="spinner-block">Loading {kind}…</div>;
  if (error && !data) return <ErrorBanner code={error} />;
  if (!data) return null;

  const { content, reports } = data;
  const hidden = content.hidden;
  const kindLabel = kind === "listing" ? "Listing" : "Safety note";
  const fullLabel = kind === "listing" ? "listing" : "safety note";

  return (
    <>
      <a className="back-link" href="#reports">Back to reports</a>
      {error && <ErrorBanner code={error} />}
      <div className="card">
        <h2>
          {kindLabel} #{content.id}{" "}
          {hidden ? (
            <span className="luna-badge luna-badge-error">hidden</span>
          ) : (
            <span className="luna-badge luna-badge-success">live</span>
          )}
        </h2>
        <p className="meta">
          Community #{content.communityId} · author user #{content.authorUserId} ·{" "}
          {content.createdAt}
        </p>
        <h3>{content.title}</h3>
        <pre className="body">{content.body}</pre>
        <div className="row-actions">
          <button
            type="button"
            className={`luna-btn ${hidden ? "luna-btn-outline" : "luna-btn-primary"}`}
            onClick={toggleHide}
            disabled={busy !== null}
          >
            {hidden ? "Unhide" : "Hide"} this {fullLabel}
          </button>
        </div>
      </div>

      <div className="card">
        <h2>Reports against this content</h2>
        {reports.length === 0 ? (
          <div className="empty">No reports filed against this content.</div>
        ) : (
          <table className="luna-table luna-table-hover">
            <thead>
              <tr>
                <th>Report</th>
                <th>Reason</th>
                <th>Reporter</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id}>
                  <td>#{r.id}</td>
                  <td>
                    {r.reason}
                    {r.detail && <div className="detail">{r.detail}</div>}
                  </td>
                  <td className="meta">user #{r.reporterUserId}</td>
                  <td className="meta">{r.createdAt}</td>
                  <td>
                    {r.resolved ? (
                      <span className="luna-badge luna-badge-success">resolved</span>
                    ) : (
                      <button
                        type="button"
                        className="luna-btn luna-btn-outline luna-btn-sm"
                        onClick={() => resolveReport(r.id)}
                        disabled={busy !== null}
                      >
                        Resolve
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
